import { useEffect, useState } from "react";
import { FaEye, FaFilter, FaSearch, FaSync, FaCalendarAlt, FaUser, FaRupeeSign, FaExclamationCircle, FaCheckCircle, FaTruck, FaTimesCircle, FaBox } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useAxios from "../api/useAxios";
import Swal from "sweetalert2";

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const axiosInstance = useAxios();

  // Order status mapping
  const ORDER_STATUS = {
    CREATED: { label: "Created", color: "bg-blue-100 text-blue-800", icon: FaBox },
    CONFIRMED: { label: "Confirmed", color: "bg-purple-100 text-purple-800", icon: FaCheckCircle },
    DELIVERED: { label: "Delivered", color: "bg-green-100 text-green-800", icon: FaTruck },
    CANCELLED: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: FaTimesCircle }
  };

  const fetchOrders = async () => {
    try {
      const res = await axiosInstance.get("/orders/all");
      setOrders(res.data.data);
      console.log(res.data.data);
    } catch (error) {
      console.error("Failed to fetch orders", error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to load orders',
        text: error.response?.data?.message || 'Please try again later',
        confirmButtonColor: '#3b82f6',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrderId(orderId);
      
      const result = await Swal.fire({
        title: 'Update Order Status?',
        html: `
          <div class="text-left">
            <p>Are you sure you want to change the order status to:</p>
            <div class="mt-2 px-3 py-2 ${ORDER_STATUS[newStatus].color} rounded-lg inline-flex items-center gap-2">
              <span class="font-semibold">${ORDER_STATUS[newStatus].label}</span>
            </div>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, update it!',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            const response = await axiosInstance.patch(`/orders/${orderId}/status`, {
              orderStatus: newStatus
            });
            return response.data;
          } catch (error) {
            Swal.showValidationMessage(`
              Failed to update status: ${error.response?.data?.message || 'Please try again'}
            `);
          }
        }
      });

      if (result.isConfirmed) {
        // Update local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.orderId === orderId 
              ? { ...order, orderStatus: newStatus }
              : order
          )
        );

        Swal.fire({
          icon: 'success',
          title: 'Status Updated!',
          text: `Order status has been updated to ${ORDER_STATUS[newStatus].label}`,
          confirmButtonColor: '#10b981',
          timer: 2000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      console.error("Failed to update order status", error);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update order status',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Status update confirmation with options - FIXED VERSION
  const confirmStatusUpdate = (orderId, currentStatus) => {
    const statusOptions = Object.entries(ORDER_STATUS)
      .filter(([status]) => status !== currentStatus)
      .map(([status, data]) => ({
        status,
        label: data.label,
        color: data.color
      }));

    // Create a custom container for the buttons
    const container = document.createElement('div');
    container.className = 'text-left space-y-2';
    
    statusOptions.forEach(option => {
      const button = document.createElement('button');
      button.className = `w-full text-left px-4 py-3 ${option.color} rounded-lg hover:opacity-90 transition-opacity flex items-center justify-between`;
      button.innerHTML = `
        <span class="font-medium">${option.label}</span>
        <i class="fas fa-chevron-right text-sm"></i>
      `;
      
      button.addEventListener('click', () => {
        Swal.close();
        setTimeout(() => updateOrderStatus(orderId, option.status), 100);
      });
      
      container.appendChild(button);
    });

    Swal.fire({
      title: 'Select New Status',
      html: container,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: '',
      showConfirmButton: false,
      cancelButtonColor: '#6b7280',
    });
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = searchTerm === "" || 
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPaymentFilter = paymentFilter === "all" || 
      order.paymentStatus.toLowerCase() === paymentFilter.toLowerCase();
    
    const matchesStatusFilter = statusFilter === "all" || 
      order.orderStatus === statusFilter;

    return matchesSearch && matchesPaymentFilter && matchesStatusFilter;
  });

  // Calculate statistics
  const totalOrders = orders.length;
  const paidOrders = orders.filter(o => o.paymentStatus === 'PAID').length;
  const pendingOrders = orders.filter(o => o.paymentStatus === 'PENDING').length;
  const failedOrders = orders.filter(o => o.paymentStatus === 'FAILED').length;

  // Status distribution
  const statusCounts = {
    CREATED: orders.filter(o => o.orderStatus === 'CREATED').length,
    CONFIRMED: orders.filter(o => o.orderStatus === 'CONFIRMED').length,
    DELIVERED: orders.filter(o => o.orderStatus === 'DELIVERED').length,
    CANCELLED: orders.filter(o => o.orderStatus === 'CANCELLED').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="relative">
          <div className="h-24 w-24 rounded-full border-[6px] border-gray-200 border-t-blue-500 animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <FaSync className="h-10 w-10 text-blue-500 animate-spin-slow" />
          </div>
        </div>
        <p className="mt-8 text-2xl font-semibold text-gray-800">Loading Orders</p>
        <p className="mt-3 text-gray-500 text-center max-w-md">Fetching order details from the server</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Add SweetAlert2 CSS */}
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/sweetalert2@11/dist/sweetalert2.min.css" />
      {/* Add FontAwesome for icons */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      {/* Header Section */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              Order Management
            </h1>
            <p className="text-gray-600 mt-2">
              Monitor and manage all customer orders in one place
            </p>
          </div>
          
          <button
            onClick={() => {
              setLoading(true);
              fetchOrders();
            }}
            className="px-6 py-3 bg-white text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center gap-3 shadow-sm hover:shadow border border-gray-200 min-w-[160px] justify-center group"
          >
            <FaSync className="h-4 w-4 text-gray-500 group-hover:rotate-180 transition-transform duration-500" />
            <span>Refresh Data</span>
          </button>
        </div>

        {/* Stats Cards - Updated with Order Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Orders Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{totalOrders}</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <div className="h-6 w-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-white">{totalOrders}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Paid Orders Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Paid Orders</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{paidOrders}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {totalOrders > 0 ? `${Math.round((paidOrders / totalOrders) * 100)}% of total` : '0%'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-50 flex items-center justify-center">
                <svg className="h-6 w-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          {/* Pending Payment Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Payment</p>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{pendingOrders}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {totalOrders > 0 ? `${Math.round((pendingOrders / totalOrders) * 100)}% of total` : '0%'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-yellow-50 flex items-center justify-center">
                <svg className="h-6 w-6 text-yellow-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Failed Payments Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Failed Payments</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{failedOrders}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {totalOrders > 0 ? `${Math.round((failedOrders / totalOrders) * 100)}% of total` : '0%'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-red-50 flex items-center justify-center">
                <FaExclamationCircle className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Status Distribution Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {Object.entries(ORDER_STATUS).map(([status, data]) => {
            const Icon = data.icon;
            return (
              <div key={status} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{data.label}</p>
                    <p className={`text-2xl font-bold mt-1 ${status === 'CREATED' ? 'text-blue-600' : status === 'CONFIRMED' ? 'text-purple-600' : status === 'DELIVERED' ? 'text-green-600' : 'text-red-600'}`}>
                      {statusCounts[status]}
                    </p>
                  </div>
                  <div className={`h-10 w-10 rounded-lg ${status === 'CREATED' ? 'bg-blue-50' : status === 'CONFIRMED' ? 'bg-purple-50' : status === 'DELIVERED' ? 'bg-green-50' : 'bg-red-50'} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${status === 'CREATED' ? 'text-blue-500' : status === 'CONFIRMED' ? 'text-purple-500' : status === 'DELIVERED' ? 'text-green-500' : 'text-red-500'}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8 border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Order Filtering</h2>
              <p className="text-gray-500 text-sm mt-1">Find specific orders using search and filters</p>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg">
                {filteredOrders.length} orders match
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Orders
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Order number, customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 focus:bg-white"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Payment Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Status
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaFilter className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer focus:bg-white"
                >
                  <option value="all">All Payment Status</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Order Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order Status
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaBox className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer focus:bg-white"
                >
                  <option value="all">All Order Status</option>
                  {Object.entries(ORDER_STATUS).map(([status, data]) => (
                    <option key={status} value={status}>
                      {data.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Actions
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setPaymentFilter("all");
                    setStatusFilter("all");
                  }}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table Section */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Order List</h3>
                <p className="text-sm text-gray-500 mt-1">All orders sorted by latest</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Showing <span className="font-semibold">{filteredOrders.length}</span> of{' '}
                  <span className="font-semibold">{orders.length}</span> orders
                </span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Order Details
                  </th>
                  <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Payment Status
                  </th>
                  <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Order Status
                  </th>
                  <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-16 text-center">
                      <div className="flex flex-col items-center justify-center max-w-md mx-auto">
                        <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                          <FaSearch className="h-10 w-10 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">No orders found</h3>
                        <p className="text-gray-500 mb-6 text-center px-4">
                          {searchTerm || paymentFilter !== "all" || statusFilter !== "all" 
                            ? "No orders match your current filters. Try adjusting your search criteria." 
                            : "No orders have been placed yet. Create your first order to get started."}
                        </p>
                        {(searchTerm || paymentFilter !== "all" || statusFilter !== "all") && (
                          <button
                            onClick={() => {
                              setSearchTerm("");
                              setPaymentFilter("all");
                              setStatusFilter("all");
                            }}
                            className="px-5 py-2.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium"
                          >
                            Clear all filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr> 
                ) : (
                  filteredOrders.map((order) => {
                    const StatusIcon = ORDER_STATUS[order.orderStatus]?.icon || FaBox;
                    return (
                      <tr 
                        key={order.orderId} 
                        className="hover:bg-gray-50 transition-colors duration-150 group"
                      >
                        <td className="py-4 px-6">
                          <div>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold rounded-lg">
                                {order.orderNumber.charAt(0)}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900 group-hover:text-blue-600">
                                  {order.orderNumber}
                                </div>
                                <div className="text-sm text-gray-500 mt-0.5">
                                  Order ID: {order.orderId}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <FaUser className="h-4 w-4 text-gray-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{order.customerName}</div>
                              <div className="text-xs text-gray-500">Customer</div>
                            </div>
                          </div>
                        </td>
                        
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <FaRupeeSign className="h-4 w-4 text-gray-500" />
                            <span className="font-bold text-gray-900 text-lg">{order.finalAmount}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Final Amount</div>
                        </td>
                        
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold
                            ${order.paymentStatus === 'PAID' ? 'bg-green-100 text-green-800' : 
                              order.paymentStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-800' : 
                              'bg-red-100 text-red-800'}`}>
                            {order.paymentStatus === 'PAID' && (
                              <svg className="h-3 w-3 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                            {order.paymentStatus.replaceAll("_", " ")}
                          </span>
                        </td>
                        
                        <td className="py-4 px-6">
                          <div className="flex flex-col gap-2">
                            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold w-fit ${ORDER_STATUS[order.orderStatus]?.color}`}>
                              <StatusIcon className="h-3 w-3 mr-1.5 flex-shrink-0" />
                              {ORDER_STATUS[order.orderStatus]?.label || order.orderStatus}
                            </span>
                            <button
                              onClick={() => confirmStatusUpdate(order.orderId, order.orderStatus)}
                              disabled={updatingOrderId === order.orderId}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {updatingOrderId === order.orderId ? 'Updating...' : 'Change Status'}
                            </button>
                          </div>
                        </td>
                        
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2 text-gray-600">
                            <FaCalendarAlt className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">{formatDate(order.orderedAt)}</span>
                          </div>
                        </td>
                        
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/orders/${order.orderId}`)}
                              className="h-9 w-9 flex items-center justify-center bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                              title="View Details"
                            >
                              <FaEye className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          {filteredOrders.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="text-sm text-gray-600">
                  Displaying <span className="font-semibold text-gray-900">{filteredOrders.length}</span> orders
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setPaymentFilter("all");
                      setStatusFilter("all");
                    }}
                    className="text-sm text-gray-600 hover:text-gray-800 font-medium px-4 py-2 bg-white hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                  >
                    Reset Filters
                  </button>
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-medium px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                    Export Data
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add custom animations to CSS */}
      <style jsx>{`
        @keyframes spin-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 2s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Orders;